// 2x-density pixel-art kit for the art/perspective prototype (hd-scene.ts, hd-sprites.ts).
// Every color is a ramp of the six theme.ts palette colors (STYLE.md: no new literals in new art).
// Units are hd pixels: 2 per world pixel, 32 per tile. Light comes from the top-left.
import { PALETTE } from './theme';
import { hash2 } from '../../common/src/util';
import { GLYPHS } from './hd-glyphs';

export const HT = 32;
export type G = CanvasRenderingContext2D;

const rgb = (h: string) => { const n = parseInt(h.slice(1), 16); return [n >> 16, (n >> 8) & 255, n & 255]; };
const hex = (r: number, g: number, b: number) => '#' + [r, g, b].map(v => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('');
/** a..b at t (0..1) */
export function mix(a: string, b: string, t: number) { const A = rgb(a), B = rgb(b); return hex(A[0] + (B[0] - A[0]) * t, A[1] + (B[1] - A[1]) * t, A[2] + (B[2] - A[2]) * t); }
const { midnight, slate, offWhite, cyan, pink, amber } = PALETTE;
/** toward Midnight (shadow ramp) */
export const dk = (c: string, t: number) => mix(c, midnight, t);
/** toward Off white (light ramp) */
export const lt = (c: string, t: number) => mix(c, offWhite, t);
export const alpha = (c: string, a: number) => { const [r, g, b] = rgb(c); return `rgba(${r},${g},${b},${a})`; };

/** Material and light ramps used by the scene. */
export const K = {
  ink: dk(midnight, 0.35), night: midnight, slate, white: offWhite, cyan, pink, amber,
  asphalt: dk(slate, 0.8), asphaltLo: dk(slate, 0.88), asphaltHi: dk(slate, 0.7), asphaltWet: dk(cyan, 0.9),
  lane: dk(amber, 0.22), laneLo: dk(amber, 0.45), paint: dk(offWhite, 0.32), paintLo: dk(offWhite, 0.55),
  walk: dk(slate, 0.32), walkHi: dk(slate, 0.22), walkLo: dk(slate, 0.44), seam: dk(slate, 0.62),
  curb: lt(slate, 0.22), curbFace: dk(slate, 0.15), curbLo: dk(slate, 0.55),
  plaza: dk(slate, 0.4), plazaHi: dk(slate, 0.3), plazaWarm: dk(mix(slate, amber, 0.18), 0.45),
  alley: dk(slate, 0.62), alleyLo: dk(slate, 0.72),
  tactile: dk(amber, 0.45), tactileLo: dk(amber, 0.62),
  concrete: dk(slate, 0.2), concreteHi: lt(slate, 0.1), concreteLo: dk(slate, 0.42),
  tar: dk(slate, 0.62), tarHi: dk(slate, 0.5), tarLo: dk(slate, 0.75),
  metal: dk(slate, 0.1), metalHi: lt(slate, 0.22), metalLo: dk(slate, 0.4),
  kawara: dk(slate, 0.5), kawaraHi: dk(slate, 0.22), kawaraLo: dk(slate, 0.72),
  wall: dk(slate, 0.45), wallHi: dk(slate, 0.3), wallLo: dk(slate, 0.62),
  wood: dk(amber, 0.6), woodHi: dk(amber, 0.45), woodLo: dk(amber, 0.75),
  plaster: dk(offWhite, 0.45), plasterLo: dk(offWhite, 0.6),
  glass: dk(cyan, 0.78), glassHi: dk(cyan, 0.5), glassLo: dk(cyan, 0.88),
  warm: amber, warmHi: lt(amber, 0.45), warmLo: dk(amber, 0.3), warmDim: dk(amber, 0.5),
  red: dk(pink, 0.15), redLo: dk(pink, 0.45), redDk: dk(pink, 0.65),
  leaf: dk(cyan, 0.68), leafHi: dk(cyan, 0.5), leafLo: dk(cyan, 0.82),
};
export const NEON = { pink, cyan, amber } as const;
export type NeonColor = keyof typeof NEON;

export const px = (g: G, x: number, y: number, w: number, h: number, c: string) => { if (w > 0 && h > 0) { g.fillStyle = c; g.fillRect(x, y, w, h); } };
/** seeded speckle over a rect: density 0..1 of `c` pixels in size×size cells */
export function speckle(g: G, x: number, y: number, w: number, h: number, c: string, density: number, seed: number, size = 1) {
  for (let j = 0; j < h; j += size) for (let i = 0; i < w; i += size) if (hash2(x + i + seed * 7919, y + j - seed * 104729) < density) px(g, x + i, y + j, size, size, c);
}
/** 1-px checker dither of `c` over a rect (soft gradients without new colors) */
export function dither(g: G, x: number, y: number, w: number, h: number, c: string, phase = 0) {
  g.fillStyle = c; for (let j = 0; j < h; j++) for (let i = (j + phase) & 1; i < w; i += 2) g.fillRect(x + i, y + j, 1, 1);
}
/** vertical gradient made of solid bands between two ramp colors (stays on the pixel grid) */
export function bands(g: G, x: number, y: number, w: number, h: number, top: string, bottom: string, steps = 4) {
  for (let s = 0; s < steps; s++) { const y0 = y + Math.round(h * s / steps), y1 = y + Math.round(h * (s + 1) / steps); px(g, x, y0, w, y1 - y0, mix(top, bottom, steps > 1 ? s / (steps - 1) : 0)); }
}
export function disc(g: G, cx: number, cy: number, rx: number, ry: number, c: string) {
  for (let y = -ry; y <= ry; y++) { const hw = Math.round(rx * Math.sqrt(Math.max(0, 1 - (y * y) / (ry * ry + 0.5)))); px(g, cx - hw, cy + y, hw * 2 + 1, 1, c); }
}
/** dark outline frame */
export function frame(g: G, x: number, y: number, w: number, h: number, c: string, t = 1) { px(g, x, y, w, t, c); px(g, x, y + h - t, w, t, c); px(g, x, y, t, h, c); px(g, x + w - t, y, t, h, c); }
/** translucent drop shadow cast down-right (light from the top-left) */
export function shadow(g: G, x: number, y: number, w: number, h: number, a = 0.35) { px(g, x, y, w, h, alpha(midnight, a)); }

// ------------------------------------------------------------------ sign glyphs (6 rows; real katakana only, STYLE.md "Signs")
const GL = GLYPHS;
export const glyphW = (ch: string) => GL[ch]?.[0].length ?? 0;
/** draw one glyph at pixel scale s */
export function glyph(g: G, ch: string, x: number, y: number, col: string, s = 1) {
  const rows = GL[ch]; if (!rows) return;
  g.fillStyle = col;
  rows.forEach((r, j) => { for (let i = 0; i < r.length; i++) if (r[i] === '#') g.fillRect(x + i * s, y + j * s, s, s); });
}
export function textW(text: string, s = 1, gap = 1) { return [...text].reduce((w, c) => w + (glyphW(c) + gap) * s, -gap * s); }
export function text(g: G, t: string, x: number, y: number, col: string, s = 1, gap = 1) {
  let cx = x; for (const c of t) { glyph(g, c, cx, y, col, s); cx += (glyphW(c) + gap) * s; }
}
